import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide
} from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHalfFull,
  MenuDown
} from 'mdi-material-ui'
import {
  addCommas,
  chromosomeRegion,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  unique
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent: any
}
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    maxHeight: 400,
    overflow: 'auto'
  }
})

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(218,150,148)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(64,113,190)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) {
    return 'C'
  } else if (str.includes('child') && str.includes('//')) {
    return `C${str.split('//')[1]}`
  } else if (str.includes('mother')) {
    return 'M'
  } else if (str.includes('father')) {
    return 'F'
  } else {
    return 'O'
  }
}

const columns1Thalassemia = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)

  const router = useRouter()

  const { id, ids, group } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'

  const column: ColumnDef<Variation>[] = React.useMemo(() => {
    return [
      {
        accessorKey: 'type',
        id: 'type',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('type')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              {row.original.variant.ref ? (
                <TypographyA>SNV/INDEL</TypographyA>
              ) : (
                <TypographyA>SV</TypographyA>
              )}
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'chrom',
        id: 'chrom',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Chrom')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              {row.original.variant.ref ? (
                <React.Fragment>
                  <TypographyA>{`${row.original.variant.chrom}:${row.original.variant.pos}`}</TypographyA>

                  <TypographyA>{`${row.original.variant.ref}-${row.original.variant.alt}`}</TypographyA>

                  <TypographyA>{`${row.original.dbSNP_IDs}`}</TypographyA>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <TypographyA>{`${row.original.variant.chrom}`}</TypographyA>

                  <TypographyA>{`${row.original.variant.start}-${row.original.variant.end}`}</TypographyA>

                  <TypographyA>{`${row.original.variant.type}`}</TypographyA>
                </React.Fragment>
              )}
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'hb_name',
        id: 'hb_name',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('hb name')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Tooltip title={row.original.hb_name}>
                <TypographyA
                  sx={{
                    maxWidth: 250,
                    lineHeight: '24px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.hb_name}`}</TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'hgvs_name',
        id: 'hgvs_name',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('hgvs name')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Tooltip title={row.original.hgvs_name}>
                <TypographyA
                  sx={{
                    maxWidth: 250,
                    lineHeight: '24px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.hgvs_name}`}</TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'syll_name',
        id: 'syll_name',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('syll name')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Tooltip title={row.original.syll_name}>
                <TypographyA
                  sx={{
                    maxWidth: 250,
                    lineHeight: '24px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.syll_name}`}</TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'type_of_thal',
        id: 'type_of_thal',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'column'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('mutation location')}
              </span>
            </Stack>

            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('type of thal')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA>{`${row.original.mutation_location}`}</TypographyA>

              <TypographyA>{`${row.original.type_of_thal}`}</TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'dna_proof',
        id: 'dna_proof',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'column'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('dna proof')}
              </span>
            </Stack>

            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('protein proof')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA>{`${row.original.dna_proof}`}</TypographyA>

              <TypographyA>{`${row.original.protein_proof}`}</TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'dna_description',
        id: 'dna_description',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('dna description')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              maxWidth={400}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Tooltip title={row.original.dna_description}>
                <TypographyA
                  sx={{
                    maxWidth: 250,
                    lineHeight: '24px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.dna_description}`}</TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'occurrence_comment',
        id: 'occurrence_comment',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'column'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('occurrence comment')}
              </span>
            </Stack>

            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('general comments')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          return (
            <Stack
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Tooltip title={row.original.occurrence_comment}>
                <TypographyA
                  sx={{
                    maxWidth: 250,
                    lineHeight: '24px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.occurrence_comment}`}</TypographyA>
              </Tooltip>

              <Tooltip title={row.original.general_comments}>
                <TypographyA
                  sx={{
                    maxWidth: 250,
                    lineHeight: '24px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.general_comments}`}</TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'PubMed_ID',
        id: 'PubMed_ID',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('PubMed ID')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }: any) => {
          const PubMed_ID =
            row.original.PubMed_ID && row.original.PubMed_ID !== '.'
              ? row.original.PubMed_ID
              : []

          return (
            <Stack
              maxWidth={'100px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              {PubMed_ID.map((item: string, i: number) => {
                return (
                  <TypographyA
                    sx={{
                      color: 'primary.main',
                      cursor: 'point'
                    }}
                    key={item}
                    onClick={() => {
                      window.open(
                        `https://pubmed.ncbi.nlm.nih.gov/${PubMed_ID[i]}/`
                      )
                    }}
                  >{`${item}`}</TypographyA>
                )
              })}
            </Stack>
          )
        },
        footer: (props) => props.column.id
      }
    ]
  }, [props])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default columns1Thalassemia
